"use client";

import * as React from "react";
import { Box } from "../../components/ui/box.tsx";
import {
} from "../../components/ui/drawer.tsx";
import { useIsMobile } from "../../hooks/use-media-query.ts";
import { selectTriggerVariants } from "../../components/ui/select.tsx";
import { Text } from "../../components/ui/text.tsx";
import {
  Tooltip,
  TooltipPopup,
} from "../../components/ui/tooltip.tsx";
import { normalizeHexColor } from "../../lib/color-convert.ts";
import {
  contrastInkFor,
  DEFAULT_THEME_COLOR_SEEDS,
  type ThemeColorMode,
} from "../../lib/custom-theme.ts";
import { cn } from "../../lib/utils.ts";
import type { Omittable } from "../../lib/props.ts";
import { ColorPicker } from "../color-picker/index.ts";
import {
  Popover,
  PopoverPopup,
  PopoverTrigger,
} from "../../components/ui/popover.tsx";

// The neutral pair drawn as itself: near-black over white, split on the
// diagonal. Literal on purpose — it depicts BOTH modes at once, so a semantic
// token, which resolves to whichever one is painting, would show half the
// answer.
const NEUTRAL_SWATCH =
  "linear-gradient(135deg, #111111 0 50%, #ffffff 50% 100%)";

/** A vetted color the field offers ahead of the free picker. */
export interface ThemeColorOption {
  /** The human name — the dot's accessible name and its tooltip. */
  name: string;
  value: string;
  /**
   * This option is the shipped NEUTRAL pair rather than a hue.
   *
   * It still carries a `value` — the hue that paints as ink where the pair
   * cannot — but its dot is drawn as the pair, split on the diagonal, because
   * no single hex can depict "near-black in light, white in dark".
   */
  neutral?: boolean;
}

/* eslint-disable no-restricted-syntax -- Curated seed options are literal
   source colors by nature: they seed a theme that is not active, so no
   semantic token can name them. */
// Suggested backgrounds beyond the default: neutral darks, tinted darks,
// then papers — every one-click outcome a scheme worth shipping.
const BACKGROUND_EXTRAS: readonly ThemeColorOption[] = [
  { name: "Ink", value: "#111111" }, // near-black, deeper than the default
  { name: "Charcoal", value: "#1c1c1c" },
  { name: "Midnight", value: "#16181d" }, // cool cast
  { name: "Plum", value: "#1a1723" }, // violet cast
  { name: "Snow", value: "#fcfcfc" }, // the shipped light default
  { name: "Paper", value: "#f6f3ec" }, // warm paper
];
/* eslint-enable no-restricted-syntax */

/** The background options for a mode, the stock default first — walking
 *  away from the product default must be one click home. */
export function themeBackgroundOptions(
  mode: ThemeColorMode,
): readonly ThemeColorOption[] {
  const defaultValue = DEFAULT_THEME_COLOR_SEEDS[mode].background;
  return [
    { name: "Default", value: defaultValue },
    ...BACKGROUND_EXTRAS.filter((option) => option.value !== defaultValue),
  ];
}

export type ThemeColorFieldProps = Omittable<{
  /** The color under edit, as a hex string. */
  value: string;
  /** Live preview while a pointer or keyboard gesture is in progress. */
  onChange: (color: string, option?: ThemeColorOption) => void;
  /** Persist after the current gesture, option pick, or hex edit finishes. */
  /**
   * The picked OPTION comes with the colour where one was clicked, so a host
   * can tell "Default" from a hue that happens to share its value instead of
   * inferring it from equality — which held only while that hue was
   * de-duplicated out of the list, an invariant nothing enforced.
   */
  onChangeComplete?: (color: string, option?: ThemeColorOption) => void;
  /**
   * Vetted picks rendered as a dot row above the picker — unlabeled: a row
   * of swatches says nothing a caption would add. One click both previews
   * and commits; the gradient re-anchors so a pick can then be nudged.
   */
  options?: readonly ThemeColorOption[];
  /** The value under edit IS the neutral pair, so the trigger draws it as one. */
  neutral?: boolean;
  /** Width of the expanded saturation area. */
  pickerSize?: number;
  /** Trigger scale — the select-trigger rungs. */
  size?: "default" | "sm";
  /** Trigger width — the select-trigger rungs. `full` spans the host row. */
  width?: "auto" | "full";
  /** Accessible name for the trigger — the visible content is only the hex,
   * so the field's role ("Accent") must arrive as a label. */
  "aria-label"?: string;
}>;

/**
 * One editable theme color, drawn as a VALUE-PAINTED FIELD — the reference
 * application's treatment: the trigger fills with the color under edit, an
 * unfilled ring beside the hex is the swatch-well affordance, and both the
 * ring and the hex sit in whichever ink — white or near-black — reads on that
 * fill. Everything else IS the select-trigger chrome (radius, border, height,
 * padding, focus ring, hover wash) so the field lines up with the selects
 * around it; only its fill and its ink are the value's own.
 *
 * The NEUTRAL pair has no single fill, so its trigger keeps the ordinary
 * field chrome and carries the pair as the split disc instead. The popover
 * leads with the caller's vetted options and keeps the free picker beneath
 * them.
 */
export function ThemeColorField({
  value,
  onChange,
  onChangeComplete,
  options,
  neutral,
  pickerSize = 240,
  size = "default",
  width = "auto",
  "aria-label": ariaLabel,
}: ThemeColorFieldProps): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  // The hovered dot IS the tooltip's anchor AND its text (`data-option-dot`):
  // one Tooltip root serves the whole row, same as the VisualPicker grid.
  const [hoveredDot, setHoveredDot] = React.useState<HTMLElement | null>(null);
  // A phone seats the picker in a bottom sheet (`Popover responsive`) —
  // same body, no popup hung off a field, and no dot tooltip (a tap is not
  // a hover).
  const isMobile = useIsMobile();
  const normalized = normalizeHexColor(value);
  // The pill paints an arbitrary USER color, so its ink cannot be a token:
  // white or near-black, whichever APCA says reads on that fill.
  const ink = neutral ? null : contrastInkFor(value);

  const body = (
    <Box direction="column" gap="base">
      {options && options.length > 0 ? (
        <Box
          direction="row"
          gap="xs"
          wrap
          onPointerLeave={isMobile ? undefined : () => setHoveredDot(null)}
          onPointerOver={
            isMobile
              ? undefined
              : (event: React.PointerEvent<HTMLElement>) => {
                  setHoveredDot(
                    (event.target as HTMLElement).closest<HTMLElement>(
                      "[data-option-dot]",
                    ),
                  );
                }
          }
        >
          {options.map((option) => {
            // The neutral option cannot be matched by hex — it shares its
            // value with whatever hue paints as ink beside it — so the
            // host's flag decides, and a hue is never selected while the
            // pair is.
            const isSelected = option.neutral
              ? neutral === true
              : neutral !== true &&
                normalizeHexColor(option.value) === normalized;
            return (
              <Box
                // Keyed by NAME, not value: the neutral pair shares its
                // hex with the hue that paints as its ink, so values can
                // legitimately collide. Names are curated and unique.
                key={option.name}
                as="button"
                type="button"
                cursor="pointer"
                rounded="full"
                aria-label={option.name}
                aria-pressed={isSelected}
                data-option-dot={option.name}
                className="size-5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                onClick={() => {
                  onChange(option.value, option);
                  onChangeComplete?.(option.value, option);
                }}
                style={{
                  ...(option.neutral
                    ? { backgroundImage: NEUTRAL_SWATCH }
                    : { backgroundColor: option.value }),
                  boxShadow: isSelected
                    ? "0 0 0 2px var(--popover), 0 0 0 3.5px var(--brand)"
                    : "inset 0 0 0 1px rgb(255 255 255 / 0.18), 0 0 0 1px rgb(0 0 0 / 0.2)",
                }}
              />
            );
          })}
        </Box>
      ) : null}
      <Box align="center">
        <ColorPicker
          format="hex"
          onChange={onChange}
          onChangeComplete={(color) => onChangeComplete?.(color)}
          showInput
          size={pickerSize}
          value={value}
        />
      </Box>
    </Box>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setHoveredDot(null);
      }}
      responsive
    >
      <PopoverTrigger
        render={
          <Box
            as="button"
            aria-label={ariaLabel}
            className={cn(
              selectTriggerVariants({
                size,
                variant: "default",
                width,
              }),
              // `full` carries its own floor; `auto` keeps the select's
              // 144px field floor so a lone field never collapses to hex
              // width.
              width === "auto" && "min-w-36",
            )}
            // The inline ink cascades to the hex text; the fill sits UNDER
            // the variant's gradient hover wash, so states keep working.
            style={ink !== null ? { backgroundColor: value, color: ink } : undefined}
            type="button"
          />
        }
      >
        <Box
          as="span"
          aria-hidden="true"
          className="size-4 shrink-0 rounded-full"
          style={
            ink !== null
              ? {
                  boxShadow: `inset 0 0 0 1.5px color-mix(in oklab, ${ink} 40%, transparent)`,
                }
              : {
                  backgroundImage: NEUTRAL_SWATCH,
                  boxShadow:
                    "inset 0 0 0 1px rgb(255 255 255 / 0.18), 0 0 0 1px rgb(0 0 0 / 0.2)",
                }
          }
        />
        <Text align="left" className="flex-1" mono size="sm" truncate>
          {value.toUpperCase()}
        </Text>
      </PopoverTrigger>
      {/* The sheet is captioned and does not scroll: the palette is sized
          to it. */}
      <PopoverPopup
        align="end"
        sheetScrollable={false}
        sheetTitle={ariaLabel ?? "Color"}
        side="bottom"
      >
        {body}
        {/* One root for the whole option row, anchored to whichever dot
            the pointer is over — the names are curated and only visible
            here. A tap is not a hover, so the sheet mounts none. */}
        {isMobile ? null : (
          <Tooltip open={hoveredDot !== null}>
            <TooltipPopup anchor={hoveredDot ?? undefined}>
              {hoveredDot?.dataset.optionDot}
            </TooltipPopup>
          </Tooltip>
        )}
      </PopoverPopup>
    </Popover>
  );
}

